import type { ReactNode } from "react";

import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { HubTableScrollRegion } from "@/components/layout/HubWorkspace";
import { CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import type { HubViewportChrome } from "@/lib/hubLayoutClassNames";
import { hubStickyTableHeadClassName } from "@/lib/hubLayoutClassNames";
import { cn } from "@/lib/utils";

export type HubWorkbenchTableProps = {
  chrome?: HubViewportChrome;
  title?: ReactNode;
  headerExtra?: ReactNode;
  loading?: boolean;
  loadingMessage?: ReactNode;
  error?: ReactNode;
  /** Optional note under the scroll region (for example read-only access). */
  footer?: ReactNode;
  className?: string;
  tableClassName?: string;
  /**
   * Full table markup: sticky `<thead>` is applied for you when you pass
   * `<thead>` / `<tbody>` children. Prefer interactive cells here; do not use
   * HubListTable truncate defaults for inputs and selects.
   */
  children: ReactNode;
};

/**
 * Workbench table shell for grids where operators act inside cells
 * (inputs, selects, workflow controls). Clips to the viewport card and scrolls
 * internally; cell overflow is left to the page.
 */
export function HubWorkbenchTable({
  chrome = "standard",
  title,
  headerExtra,
  loading = false,
  loadingMessage = "Loading…",
  error = null,
  footer,
  className,
  tableClassName,
  children,
}: HubWorkbenchTableProps): JSX.Element {
  return (
    <HubViewportCard chrome={chrome} className={cn("overflow-hidden", className)}>
      {title != null || headerExtra != null ? (
        <CardHeader className="flex shrink-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          {title != null ? <CardTitle>{title}</CardTitle> : <span />}
          {headerExtra}
        </CardHeader>
      ) : null}
      <CardBody className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-0">
        {loading ? (
          <p className="px-4 py-6 text-sm text-slate-600">{loadingMessage}</p>
        ) : error != null ? (
          <div className="px-4 py-6 text-sm text-red-700">{error}</div>
        ) : (
          <>
            <HubTableScrollRegion className="px-0">
              <table className={cn("min-w-full text-left text-sm", tableClassName)}>{children}</table>
            </HubTableScrollRegion>
            {footer != null ? <div className="shrink-0 px-4 pb-4">{footer}</div> : null}
          </>
        )}
      </CardBody>
    </HubViewportCard>
  );
}

/** Sticky workbench header row class — put on `<thead>`. */
export function hubWorkbenchTheadClassName(): string {
  return hubStickyTableHeadClassName("slate");
}

/** Default workbench text cell that stays in-column without directory truncate. */
export function hubWorkbenchTextCellClassName(options?: {
  align?: "left" | "right";
  nowrap?: boolean;
}): string {
  return cn(
    "min-w-0 px-3 py-3 align-middle text-slate-700",
    options?.align === "right" && "text-right tabular-nums",
    options?.nowrap ? "whitespace-nowrap" : "break-words",
  );
}
